import _ from 'underscore';
import dayjs from '../../../shared/services/dayjs.js';
import { zeigeKleineNachricht } from './systemNachricht.js';
import { schichtenService } from './services/SchichtenService.js';
import { stammdatenService } from './services/StammdatenService.js';
import { findeMitarbeiter } from './util.js';
import { erstelleZeitblockTabelle, neuesZeitblockObjekt, offenerZeitblock, zeitblockAnzahl } from './zeitblock.js';
import {
	erstellePostenSelect,
	erstelleEinsatzortSelect,
	erstelleQualifikationSelect,
	erstelleToolbarVorlagenSelect,
	zeigeNeueSchicht,
    } from './dienstplan.js';
import { renderVorlagenPicker, wendeVorlageAn } from './components/vorlagenPicker.js';

// Relevante HTML Elemente auf die oft zugegriffen wird
const modal = document.getElementById('my-modal');
const schichtInputTemplate = document.querySelector('[template-schicht-input]');

// Feste Vorschlagsfarben für die Schichtfarben-Swatches — die eigentliche Farbwahl bleibt frei
// (der native Farbwähler #input-schicht-farbe steht als erster, "custom" Swatch davor). Bewusst nur
// 5 Vorschläge (statt 6), damit Farbwähler + Divider + Vorschläge in der row2-Spalte in eine Zeile passen.
const FARB_VORSCHLAEGE = ['#2a6fdb', '#b8791a', '#7a5af0', '#1f8a5b', '#c0485a'];

/**
 * Baut die "Art"-Segmented-Control (Feste Zeiten / Offener Beginn / Offenes Ende / Ganztags) und
 * verdrahtet sie mit den drei (nun versteckten) Checkboxen Ganztags/OffenerBeginn/OffenesEnde, die
 * weiterhin die tatsächlichen Werte halten (sammleSchichtDaten liest weiterhin von diesen). Ein Klick
 * auf ein Segment löst nur das 'input'-Event der Checkbox aus, die ein echter Klick auch ausgelöst
 * hätte — damit bleibt die bestehende Zeitblock-Autobefüllungslogik (siehe oben) unverändert gültig.
 * @param {HTMLElement} container das geklonte Dialog-Template
 */
function initArtSegment(container) {
	const seg = container.querySelector('#input-art-seg');
	if (!seg) return;
	const ganztags = container.querySelector('#input-schicht-ganztags');
	const offenerBeginn = container.querySelector('#input-schicht-offener-beginn');
	const offenesEnde = container.querySelector('#input-schicht-offenes-ende');

	const aktiviereSegment = (art) => {
		seg.querySelectorAll('.o').forEach((o) => o.classList.toggle('on', o.dataset.art === art));
	};

	// Anfangszustand aus den (evtl. schon vorbelegten) Checkboxen ableiten.
	let initialArt = 'fest';
	if (ganztags.checked) initialArt = 'ganztags';
	else if (offenerBeginn.checked && offenesEnde.checked) initialArt = 'ganztags'; // altes Datenmodell erlaubte diese Kombination auch ohne Ganztags-Flag
	else if (offenerBeginn.checked) initialArt = 'offenerBeginn';
	else if (offenesEnde.checked) initialArt = 'offenesEnde';
	aktiviereSegment(initialArt);

	seg.querySelectorAll('.o').forEach((option) => {
		option.addEventListener('click', () => {
			const { art } = option.dataset;
			if (art === 'ganztags') {
				offenerBeginn.checked = true;
				offenesEnde.checked = true;
				ganztags.checked = true;
				ganztags.dispatchEvent(new Event('input'));
			} else if (art === 'offenerBeginn') {
				offenesEnde.checked = false;
				ganztags.checked = false;
				offenerBeginn.checked = true;
				offenerBeginn.dispatchEvent(new Event('input'));
			} else if (art === 'offenesEnde') {
				offenerBeginn.checked = false;
				ganztags.checked = false;
				offenesEnde.checked = true;
				offenesEnde.dispatchEvent(new Event('input'));
			} else {
				offenerBeginn.checked = false;
				offenesEnde.checked = false;
				ganztags.checked = false;
			}
			aktiviereSegment(art);
		});
	});
}

/**
 * Baut die Schichtfarben-Swatches: der native Farbwähler (für freie Farbwahl) steht als erstes
 * Element, getrennt durch einen dezenten Divider von den festen Vorschlags-Swatches danach. Beide
 * verdrahten das bestehende #input-schicht-farbe Input, das den eigentlichen Wert hält
 * (sammleSchichtDaten liest weiterhin von diesem Input).
 * @param {HTMLElement} container das geklonte Dialog-Template
 */
function initFarbSwatches(container) {
	const wrap = container.querySelector('#input-farbe-div');
	const farbInput = container.querySelector('#input-schicht-farbe');
	if (!wrap || !farbInput) return;
	const aktualisiereOn = () => {
		wrap.querySelectorAll('.sw').forEach((sw) => sw.classList.toggle('on', sw.dataset.farbe?.toLowerCase() === farbInput.value.toLowerCase()));
	};
	wrap.appendChild(farbInput);
	const divider = document.createElement('span');
	divider.className = 'sw-divider';
	wrap.appendChild(divider);
	FARB_VORSCHLAEGE.forEach((farbe) => {
		const sw = document.createElement('button');
		sw.type = 'button';
		sw.className = 'sw';
		sw.dataset.farbe = farbe;
		sw.style.background = farbe;
		sw.title = farbe;
		sw.addEventListener('click', () => {
			farbInput.value = farbe;
			aktualisiereOn();
		});
		wrap.appendChild(sw);
	});
	farbInput.addEventListener('input', aktualisiereOn);
	aktualisiereOn();
}

/**
 * Erstellt die HTML Input Felder für die Schicht, der "type" Parameter hilft erstellen (0) und editieren (1) zu differenzieren
 * @param {object} schicht Schichtobjekt
 * @param {int} type
 * @param {string} gridId zeigt auf das Grid Element für die Schicht.
 * @param {object} vorlage optional — bereits geladene Schichtvorlage (oder vorlage-artiges Schichtobjekt),
 *   deren Titel/Farbe/Art/Zeitblöcke direkt ins Formular übernommen werden. Für den Fall, dass eine
 *   Vorlage über die Mitarbeiteransicht auf eine Zelle ohne festen Posten angewendet wird (siehe
 *   index.js:erstelleSchichtInputFeldAusVorlage) — Posten/Qualifikation bleiben dabei unangetastet
 *   (freie/vorbelegte Auswahl unten), nur die inhaltlichen Felder werden übernommen.
 */
 async function erstelleSchichtInputFeld(schicht, type, gridId, vorlage = undefined) {
	const mitarbeiter = await stammdatenService.getMitarbeiter();
	const mitarbeiterObj = findeMitarbeiter(mitarbeiter, schicht.MitarbeiterID);
	const postenListe = await stammdatenService.getPosten();
	const qualifikationen = await stammdatenService.getQualifikationen();
	// Ohne festen Posten (Mitarbeiteransicht, siehe dort) als sinnvolle Vorbelegung den ersten
	// zugeordneten Posten der Person verwenden — die Auswahl bleibt trotzdem frei änderbar.
	const bevorzugtePostenID = mitarbeiterObj.Beschaeftigung[0].Posten[0]?.PostenID;

	// Reset des Modals
	const modalText = document.getElementById('modal-text');
	modalText.innerHTML = '';
	// Kopieren des Templates
	const inputFeld = schichtInputTemplate.content.cloneNode(true).children[0];
	// Titel und Untertitel mit Name und Datum
	inputFeld.querySelector('#input-tag').innerText = dayjs(schicht.Datum).format('dd.');
	inputFeld.querySelector('#input-datum').innerText = dayjs(schicht.Datum).format('DD.MM.YYYY');
	inputFeld.querySelector('#input-name').innerText = `${mitarbeiterObj.Personalien.Nachname}, ${mitarbeiterObj.Personalien.Vorname}`;

	// Schnellauswahl Vorlage (Chips + durchsuchbare Popover, siehe components/vorlagenPicker.js)
	const vorlagenBereichID = schicht.BereichID || postenListe.find((p) => p._id === schicht.PostenID)?.BereichID || postenListe.find((p) => p._id === bevorzugtePostenID)?.BereichID;
	await renderVorlagenPicker(inputFeld.querySelector('#input-vorlagen'), vorlagenBereichID, (v) => wendeVorlageAn(v, inputFeld));

	// Auswahl des Postens
	let inputDiv = inputFeld.querySelector('#input-posten-div');
	const postenSelect = await erstellePostenSelect(postenListe, schicht.PostenID, bevorzugtePostenID);
	inputDiv.appendChild(postenSelect);

	// Auswahl des Einsatzorts — nur anzeigen, wenn der/die Mitarbeitende mind. einem Einsatzort
	// zugeordnet ist (oder die Schicht bereits einen EinsatzortID trägt, z.B. aus einer mittlerweile
	// entfernten Zuordnung — dann bleibt der Wert weiterhin editierbar statt unsichtbar zu verschwinden).
	const einsatzorteMA = mitarbeiterObj.Beschaeftigung[0].Einsatzorte ?? [];
	if (einsatzorteMA.length > 0 || schicht.EinsatzortID) {
		const alleEinsatzorte = await stammdatenService.getEinsatzorte();
		const einsatzortDiv = inputFeld.querySelector('#input-einsatzort-div');
		einsatzortDiv.innerHTML = '<label>Einsatzort</label>';
		einsatzortDiv.appendChild(await erstelleEinsatzortSelect(alleEinsatzorte, einsatzorteMA, schicht.EinsatzortID));
	}

	// Auswahl der Qualifikation
	inputDiv = inputFeld.querySelector('#input-qualifikationen');
	mitarbeiterObj.Beschaeftigung[0].Verguetung.sort((a, b) => b.IstStandardQualifikation - a.IstStandardQualifikation);
	const qualiSelect = await erstelleQualifikationSelect(qualifikationen, mitarbeiterObj.Beschaeftigung[0].Verguetung, schicht.QualifikationID);
	inputDiv.appendChild(qualiSelect);
	// Input für ganztags
	const inputGanztags = inputFeld.querySelector('#input-schicht-ganztags');
	// Input für offener Beginn
	const inputOffenerBeginn = inputFeld.querySelector('#input-schicht-offener-beginn');
	inputOffenerBeginn.checked = schicht.OffenerBeginn ? schicht.OffenerBeginn : false;
	inputOffenerBeginn.addEventListener('input', () => {
		if (!inputOffenerBeginn.checked) {
			inputGanztags.checked = false;
		}
		// Falls wir die checkbox auf true gesetzt haben, blenden wir einen Zeitblock mit Sollstunden ein.
		// ...außer es wurden mehrere Zeitblöcke definiert (<=2 da die erste table row die Titelzeile ist)
		if (inputOffenerBeginn.checked && zeitblockAnzahl() <= 1) {
			offenerZeitblock(schicht, false, mitarbeiterObj);
		}
	});
	// Input für offenes Ende
	const inputOffenesEnde = inputFeld.querySelector('#input-schicht-offenes-ende');
	inputOffenesEnde.checked = schicht.OffenesEnde ? schicht.OffenesEnde : false;
	inputOffenesEnde.addEventListener('input', () => {
		if (!inputOffenesEnde.checked) {
			inputGanztags.checked = false;
		}
		// Falls wir die checkbox auf true gesetzt haben, blenden wir einen Zeitblock mit Sollstunden ein.
		// ...außer es wurden mehrere Zeitblöcke definiert (<=2 da die erste table row die Titelzeile ist)
		if (inputOffenesEnde.checked && zeitblockAnzahl() <= 1) {
			offenerZeitblock(schicht, true, mitarbeiterObj);
		}
	});


	inputGanztags.checked = schicht.Ganztags ? schicht.Ganztags : false;
	inputGanztags.addEventListener('input', () => {
		if (inputGanztags.checked) {
			inputOffenerBeginn.checked = true;
			inputOffenesEnde.checked = true;
		}
		// ...außer es wurden mehrere Zeitblöcke definiert (<=2 da die erste table row die Titelzeile ist)
		if (inputGanztags.checked && zeitblockAnzahl() <= 1) {
			offenerZeitblock(schicht, false, mitarbeiterObj, true);
		}
	});

	// Falls es eine Farbe/Notiz schon gibt, auswählen, sonst Standard value.
	inputFeld.querySelector('#input-schicht-farbe').value = schicht.SchichtFarbe ? schicht.SchichtFarbe : '#0071bd';
	inputFeld.querySelector('#input-schicht-notiz').value = schicht.SchichtNotiz ? schicht.SchichtNotiz : '';
	initFarbSwatches(inputFeld);
	initArtSegment(inputFeld);

	// Titel anzeigen falls vorhanden
	if (schicht.Titel) {
		inputFeld.querySelector('#input-schicht-titel').value = schicht.Titel;
	}

	// Zeige das befüllte Input Feld an
	modalText.appendChild(inputFeld);

	const sumbitBtn = inputFeld.querySelector('#input-submit');
	sumbitBtn.addEventListener('click', () => updateSchichtHandler(schicht, type, gridId));
	const deleteBtn = inputFeld.querySelector('#input-delete');
	if (schicht._id) {
		deleteBtn.addEventListener('click', async (event) => window.fun.schichtEntfernen(schicht._id, event, gridId));
	} else {
		deleteBtn.addEventListener('click', async () => closeModal());
	}

	modal.style.display = 'block';
	// Erstelle Zeitblock Input
	erstelleZeitblockTabelle(schicht, type);
	// Falls eine Vorlage übergeben wurde, deren Inhalt (Titel/Farbe/Art/Zeitblöcke) direkt übernehmen —
	// überschreibt bewusst erst danach, siehe Parameter-Doku oben.
	if (vorlage) {
		wendeVorlageAn(vorlage, inputFeld);
	}
}

/**
 * Hier sollen die Informationen der neuen Dienstzeiten aufgenommen werden und and die DB gesendet werden.
 * @param {object} schicht
 * @param {int} type entscheidet ob erstellen (0) oder editieren (1)
 * @param {string} gridId zeigt auf das Grid Element für die Schicht.
 */
 async function updateSchichtHandler(schicht, type, gridId) {
	schicht = sammleSchichtDaten(schicht);
	// sammleSchichtDaten liefert undefined bei einem Validierungsfehler (Nachricht zeigt es bereits an).
	if (!schicht) {
		return;
	}
	if (!schicht.Ganztags && _.isEqual(schicht.SchichtVon, schicht.SchichtBis)) {
		zeigeKleineNachricht('Schicht kann nicht am gleichen Zeitpunkt beginnen und enden!', 0);
		return;
	}

	// Eine neue Schicht soll den Zeittyp 2 für Dienstplan bekommen und keine SchichtID besitzen
	if (type === 0 || schicht.ZeitTyp === 8) {
		delete schicht._id;
		schicht.ZeitTyp = 2;
	}

	const schichtNeu = await schichtenService.updateSchichtObjekt(schicht);
	if (schichtNeu) {
		await zeigeNeueSchicht(schichtNeu, gridId);
		zeigeKleineNachricht('Die Schicht konnte erfolgreich eingefügt werden!', 1);
	}
	closeModal();
}

function sammleSchichtDaten(schicht, isVorlage = false) {
    // Qualifikation und Posten brauchen wir nicht für die schichtvorlage
    if (!isVorlage) {
		const qualSelect = document.getElementById('input-qualifikation');
		const postenSelect = document.getElementById('input-posten-select');
		// Leere Selects (z.B. kein Posten in der Liste/keine aktive Qualifikation) dürfen nicht
		// stillschweigend mit leerer PostenID/QualifikationID gespeichert werden — sonst "verschwindet"
		// die Schicht aus jeder Posten-gruppierten Ansicht (Wochenplan), da sie nirgends zugeordnet ist.
		if (!postenSelect.value) {
			zeigeKleineNachricht('Bitte einen Posten auswählen!', 0);
			return;
		}
		if (!qualSelect.value) {
			zeigeKleineNachricht('Bitte eine Qualifikation auswählen!', 0);
			return;
		}
        schicht.QualifikationID = qualSelect.value;
        schicht.Qualifikation = qualSelect.children[qualSelect.selectedIndex].innerText;
		schicht.PostenID = postenSelect.value;
		schicht.Posten = postenSelect.children[postenSelect.selectedIndex].innerText;
		// Nur vorhanden, wenn der/die Mitarbeitende mind. einem Einsatzort zugeordnet ist (oder die
		// Schicht schon einen trägt), siehe erstelleSchichtInputFeld.
		const einsatzortSelect = document.getElementById('input-einsatzort-select');
		if (einsatzortSelect) {
			schicht.EinsatzortID = einsatzortSelect.value;
			schicht.Einsatzort = einsatzortSelect.value ? einsatzortSelect.children[einsatzortSelect.selectedIndex].innerText : '';
		}
    }
	const betriebSelect = document.getElementById('select-betriebsstaette');
	schicht.BetriebsstaetteID = betriebSelect.value;
	// Der Vorlage-Editor hat ein eigenes Bereich-Select (#input-vorlage-bereich, siehe toolbar.js:
	// erstelleSchichtVorlagenInputFeld), unabhängig von der Sidebar-Bereichsauswahl (#bereich-select) —
	// existiert es (nur dort im DOM vorhanden), hat es Vorrang.
	const bereichSelect = document.getElementById('input-vorlage-bereich') ?? document.getElementById('bereich-select');
	schicht.BereichID = bereichSelect.value;
	schicht.Bereich = bereichSelect[bereichSelect.selectedIndex].innerText;

	schicht.OffenerBeginn = document.getElementById('input-schicht-offener-beginn').checked;
	schicht.OffenesEnde = document.getElementById('input-schicht-offenes-ende').checked;
	schicht.Ganztags = document.getElementById('input-schicht-ganztags').checked;
	schicht.SchichtFarbe = document.getElementById('input-schicht-farbe').value;
	schicht.SchichtNotiz = document.getElementById('input-schicht-notiz').value;
	const anzahlZeilen = zeitblockAnzahl();

	// Setze die Zeitblöcke zurück, damit beim Löschen einzelner Zeilen keine Inkonsistenzen auftreten
	schicht.Zeitbloecke = [];
	for (let i = 1; i <= anzahlZeilen; i += 1) {
		schicht.Zeitbloecke[i - 1] = neuesZeitblockObjekt();
		schicht.Zeitbloecke[i - 1].BlockVon = document.getElementById(`${i}:von`).value;
		schicht.Zeitbloecke[i - 1].BlockBis = document.getElementById(`${i}:bis`).value;
		schicht.Zeitbloecke[i - 1].EgfzID = parseInt(document.getElementById(`${i}:egfz`).value, 10);
		if (schicht.Zeitbloecke[i - 1].BlockVon == '' || schicht.Zeitbloecke[i - 1].BlockBis == '') {
			zeigeKleineNachricht('Bitte vollständige Von-Bis Uhrzeiten eingeben!', 0);
			return;
		}
	}
	schicht.SchichtVon = schicht.Zeitbloecke[0].BlockVon;
	schicht.SchichtBis = schicht.Zeitbloecke[schicht.Zeitbloecke.length - 1].BlockBis;
	schicht.Datum = dayjs(schicht.Datum).tz().format('YYYY-MM-DD');
	if (isVorlage) {
		schicht.Titel = document.getElementById('input-vorlage-titel').value;
	} else {
		schicht.Titel = document.getElementById('input-schicht-titel').value;
	}
	// Falls kein Titel angegeben wurde, legen wir einen Standardtitel fest.
	if (schicht.Titel === '') {
		schicht.Titel = 'unbenannt';
	}
	return schicht;
}

/**
 * Speichert eine Schichtvorlage
 * @param {object} schicht SchichtObjekt aus dem Schicht Editor
 */
 async function speichereSchichtVorlage(schicht) {
	// Klone die Schicht damit dessen Inhalt nicht versehentlich überschrieben wird
	let schichtClone = JSON.parse(JSON.stringify(schicht));
	schichtClone = sammleSchichtDaten(schichtClone, true);
	// sammleSchichtDaten liefert undefined bei einem Validierungsfehler (Nachricht zeigt es bereits an).
	if (!schichtClone) {
		return;
	}
	if (schichtClone.Titel === '') {
		zeigeKleineNachricht('Bitte geben Sie noch einen Titel für die Vorlage ein!', 0);
		return;
	}
    // Wir übernehmen die SchichtID der ausgewählten Schichtvorlage
	schichtClone._id = document.querySelector('#input-vorlage').value;
   	schichtClone.ZeitTyp = 8;
	// MitarbeiterID, PostenID und QualifikationID brauchen wir nicht
	schichtClone.MitarbeiterID = '';
	schichtClone.QualifikationID = '';
	schichtClone.Qualifikation = '';
	schichtClone.Posten = '';
	schichtClone.PostenID = '';
	// ... BetriebsstätteID schon
	schichtClone.BetriebsstaetteID = stammdatenService.aktuelleBetriebsstaette;
	const vorlageNeu = await schichtenService.speichereSchichtVorlage(schichtClone);
	if (vorlageNeu !== false) {
		await erstelleToolbarVorlagenSelect();
	}
	closeModal();
}

/**
 * Aktuell ausgewählte Schichtvorlage soll gelöscht werden.
 */
async function loescheSchichtVorlage() {
    const vorlageID = document.querySelector('#input-vorlage').value;
    // Nichts löschen, falls keine ausgewählt ist.
    if (vorlageID === '') return;
    const result = await schichtenService.entferneSchichtObjekt(vorlageID);
    if (result) {
		const select = document.getElementById('vorlage-tb-select');
		select.options[vorlageID].remove();
		document.querySelector(`.vorlagen-liste-item[aria-label="${vorlageID}"]`)?.remove();
    }
	closeModal();
}

export {
    erstelleSchichtInputFeld,
    sammleSchichtDaten,
    speichereSchichtVorlage,
    updateSchichtHandler,
    loescheSchichtVorlage,
    initArtSegment,
    initFarbSwatches,
};
